import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './prepaidTerms.css'

const PDF_URL = `${process.env.PUBLIC_URL || ''}/prepaid_terms.pdf`
const DOWNLOAD_NAME = 'Knect-Mobile-Prepaid-Terms-and-Conditions.pdf'

// Zoom is relative to "fit to width". The site disables pinch-zoom, so these buttons are
// the only way to enlarge the text on a phone.
const ZOOM_STEPS = [1, 1.25, 1.5, 2, 2.5, 3]

// Cap each page canvas so high-DPI phones stay under mobile browser canvas memory limits
const MAX_CANVAS_PIXELS = 8000000

const DownloadIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
		<polyline points="7 10 12 15 17 10" />
		<line x1="12" y1="15" x2="12" y2="3" />
	</svg>
)

const CloseIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
)

const MinusIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
		<line x1="5" y1="12" x2="19" y2="12" />
	</svg>
)

const PlusIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
		<line x1="12" y1="5" x2="12" y2="19" />
		<line x1="5" y1="12" x2="19" y2="12" />
	</svg>
)

const PrepaidTerms = () => {
	const navigate = useNavigate()
	const bodyRef = useRef(null)
	const pagesRef = useRef(null)
	const pdfRef = useRef(null)
	const renderIdRef = useRef(0)
	const [pdfLoaded, setPdfLoaded] = useState(false)
	const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'error'
	const [zoomIndex, setZoomIndex] = useState(0)
	const [fitWidth, setFitWidth] = useState(0)

	// Go back if we came from within the site, otherwise (e.g. opened from an SMS link) go home
	const handleClose = useCallback(() => {
		const cameFromSite = document.referrer && new URL(document.referrer).origin === window.location.origin
		if (cameFromSite && window.history.length > 1) {
			navigate(-1)
		} else {
			navigate('/')
		}
	}, [navigate])

	useEffect(() => {
		// Disable page scrolling behind the modal
		document.body.style.overflow = 'hidden'

		const onKeyDown = (e) => {
			if (e.key === 'Escape') handleClose()
		}
		window.addEventListener('keydown', onKeyDown)

		return () => {
			document.body.style.overflow = 'unset'
			window.removeEventListener('keydown', onKeyDown)
		}
	}, [handleClose])

	// Load the PDF once. pdf.js is imported on the client only — it needs browser APIs.
	// The legacy build supports older Android/iOS browsers.
	useEffect(() => {
		let loadingTask
		let cancelled = false

		const load = async () => {
			try {
				// CRA/webpack hands back the namespace on `.default` for this ESM build,
				// unlike Next, so accept either shape.
				// pdf.js v6 is ESM-only and CRA's webpack parses its .mjs as CommonJS,
				// which strips every export. Load it natively from public/ instead —
				// webpackIgnore keeps the bundler out of this import.
				const base = process.env.PUBLIC_URL || ''
				const mod = await import(/* webpackIgnore: true */ `${base}/pdf.min.mjs`)
				const pdfjs = mod.GlobalWorkerOptions ? mod : (mod.default || mod)
				// The worker is copied into public/ because CRA's build doesn't reliably
				// resolve new URL('...', import.meta.url) for files inside node_modules.
				pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`

				loadingTask = pdfjs.getDocument({ url: PDF_URL })
				const pdf = await loadingTask.promise
				if (cancelled) return
				pdfRef.current = pdf
				setPdfLoaded(true)
			} catch (err) {
				if (!cancelled) {
					console.error('Failed to load terms PDF:', err)
					setStatus('error')
				}
			}
		}
		load()

		return () => {
			cancelled = true
			pdfRef.current = null
			if (loadingTask) loadingTask.destroy()
		}
	}, [])

	// Track the available width so pages fit the screen (and re-fit on rotate/resize)
	useEffect(() => {
		const body = bodyRef.current
		if (!body) return undefined

		let timer
		const measure = () => {
			const styles = window.getComputedStyle(body)
			const width = Math.floor(
				body.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight)
			)
			setFitWidth((prev) => (Math.abs(prev - width) < 2 ? prev : width))
		}
		measure()

		const observer = new ResizeObserver(() => {
			clearTimeout(timer)
			timer = setTimeout(measure, 150)
		})
		observer.observe(body)

		return () => {
			clearTimeout(timer)
			observer.disconnect()
		}
	}, [])

	// Render every page to a canvas; re-runs when the zoom or available width changes.
	// Pages are swapped in only once all are drawn, so zooming doesn't flicker.
	useEffect(() => {
		const pdf = pdfRef.current
		const container = pagesRef.current
		const body = bodyRef.current
		if (!pdfLoaded || !pdf || !container || !body || !fitWidth) return

		const renderId = ++renderIdRef.current
		const zoom = ZOOM_STEPS[zoomIndex]
		const dpr = window.devicePixelRatio || 1

		const render = async () => {
			try {
				const canvases = []
				for (let n = 1; n <= pdf.numPages; n++) {
					const page = await pdf.getPage(n)
					if (renderId !== renderIdRef.current) return

					const base = page.getViewport({ scale: 1 })
					const cssScale = (fitWidth / base.width) * zoom
					const cssWidth = Math.floor(base.width * cssScale)
					const cssHeight = Math.floor(base.height * cssScale)
					const outputScale = Math.min(dpr, Math.sqrt(MAX_CANVAS_PIXELS / (cssWidth * cssHeight)))
					const viewport = page.getViewport({ scale: cssScale * outputScale })

					const canvas = document.createElement('canvas')
					canvas.width = Math.floor(viewport.width)
					canvas.height = Math.floor(viewport.height)
					canvas.style.width = `${cssWidth}px`
					canvas.style.height = `${cssHeight}px`
					canvas.className = 'pt-terms-page'
					canvas.setAttribute('role', 'img')
					canvas.setAttribute('aria-label', `Terms and Conditions, page ${n} of ${pdf.numPages}`)

					await page.render({ canvas, viewport }).promise
					if (renderId !== renderIdRef.current) return

					// A canvas can't carry the PDF's hyperlinks, so overlay a real <a> over each
					// link annotation, positioned in CSS pixels for the current zoom level.
					const wrap = document.createElement('div')
					wrap.className = 'pt-terms-page-wrap'
					wrap.style.width = `${cssWidth}px`
					wrap.style.height = `${cssHeight}px`
					wrap.appendChild(canvas)

					const cssViewport = page.getViewport({ scale: cssScale })
					const annotations = await page.getAnnotations()
					if (renderId !== renderIdRef.current) return

					for (const annotation of annotations) {
						// pdf.js only populates `url` for links it deems safe (javascript: etc. are dropped)
						if (annotation.subtype !== 'Link' || !annotation.url) continue
						// pdf.js v6 dropped convertToViewportRectangle, so convert both corners
						// (convertToViewportPoint applies the viewport transform, rotation included).
						const [x1, y1] = cssViewport.convertToViewportPoint(annotation.rect[0], annotation.rect[1])
						const [x2, y2] = cssViewport.convertToViewportPoint(annotation.rect[2], annotation.rect[3])
						const link = document.createElement('a')
						link.className = 'pt-terms-link'
						link.href = annotation.url
						link.target = '_blank'
						link.rel = 'noopener noreferrer'
						link.title = annotation.url
						link.style.left = `${Math.min(x1, x2)}px`
						link.style.top = `${Math.min(y1, y2)}px`
						link.style.width = `${Math.abs(x2 - x1)}px`
						link.style.height = `${Math.abs(y2 - y1)}px`
						wrap.appendChild(link)
					}

					canvases.push(wrap)
				}

				// Keep the reader's place when the zoom level changes
				const yRatio = body.scrollHeight ? body.scrollTop / body.scrollHeight : 0
				const xCenter = body.scrollWidth ? (body.scrollLeft + body.clientWidth / 2) / body.scrollWidth : 0.5

				container.replaceChildren(...canvases)

				body.scrollTop = yRatio * body.scrollHeight
				body.scrollLeft = xCenter * body.scrollWidth - body.clientWidth / 2
				setStatus('ready')
			} catch (err) {
				if (renderId === renderIdRef.current) {
					console.error('Failed to render terms PDF:', err)
					setStatus('error')
				}
			}
		}
		render()
	}, [pdfLoaded, zoomIndex, fitWidth])

	const zoomPercent = Math.round(ZOOM_STEPS[zoomIndex] * 100)

	return (
		<div className="pt-terms-overlay" onClick={handleClose}>
			<div
				className="pt-terms-modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby="pt-terms-title"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="pt-terms-header">
					<h1 id="pt-terms-title" className="pt-terms-title">
						Prepaid Terms &amp; Conditions
					</h1>
					<div className="pt-terms-actions">
						<a href={PDF_URL} download={DOWNLOAD_NAME} className="pt-terms-download" aria-label="Download PDF">
							<DownloadIcon />
							<span>Download</span>
						</a>
						<button type="button" className="pt-terms-close" onClick={handleClose} aria-label="Close">
							<CloseIcon />
						</button>
					</div>
				</div>

				<div className="pt-terms-body-wrap">
					<div ref={bodyRef} className="pt-terms-body">
						{/* Filled with page canvases by the render effect */}
						<div ref={pagesRef} className="pt-terms-pages" />
					</div>

					{status === 'loading' && (
						<div className="pt-terms-status" role="status">
							<span className="pt-terms-spinner" aria-hidden="true" />
							Loading terms…
						</div>
					)}

					{status === 'error' && (
						<div className="pt-terms-fallback">
							<p>We couldn&apos;t display the terms here.</p>
							<a href={PDF_URL} download={DOWNLOAD_NAME} className="pt-terms-fallback-btn">
								<DownloadIcon />
								Download PDF
							</a>
							<a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="pt-terms-fallback-link">
								Open in a new tab
							</a>
						</div>
					)}

					{status === 'ready' && (
						<div className="pt-terms-zoom" role="group" aria-label="Zoom">
							<button
								type="button"
								onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
								disabled={zoomIndex === 0}
								aria-label="Zoom out"
							>
								<MinusIcon />
							</button>
							<span className="pt-terms-zoom-level" aria-live="polite">{zoomPercent}%</span>
							<button
								type="button"
								onClick={() => setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
								disabled={zoomIndex === ZOOM_STEPS.length - 1}
								aria-label="Zoom in"
							>
								<PlusIcon />
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default PrepaidTerms
